import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import RealEstateFinanceAPI from 'dashboard/api/realEstateFinance';
import {
  persistFinanceTab,
  readFinanceTab,
} from './realEstateFinanceNavigation';

const STORAGE_KEY = 'real_estate_finance_filters';
const ORDER_STORAGE_KEY = 'real_estate_finance_order';
const DEFAULT_FILTERS = {
  filtro_endereco: '',
  filtro_numero: '',
  filtro_periodo_inicial: '',
  filtro_periodo_final: '',
  filtro_loja: [],
  filtro_cliente_gerente: [],
  filtro_status: [],
  filtro_tipo_venda: [],
  filtro_status_financeiro: [],
};

const DEFAULT_ORDER = { patch: '', id: -1 };

const readStoredOrder = () => {
  try {
    return {
      ...DEFAULT_ORDER,
      ...(JSON.parse(window.localStorage.getItem(ORDER_STORAGE_KEY) || '{}') ||
        {}),
    };
  } catch {
    return { ...DEFAULT_ORDER };
  }
};

const readStoredFilters = () => {
  try {
    return {
      ...DEFAULT_FILTERS,
      ...(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {}),
    };
  } catch {
    return { ...DEFAULT_FILTERS };
  }
};

export function useRealEstateFinance({ notify, t = key => key } = {}) {
  const tab = ref(readFinanceTab());
  const filters = ref(readStoredFilters());
  const order = ref(readStoredOrder());
  const page = ref(1);
  const rows = ref([]);
  const meta = ref({ count: 0, current_page: 1, per_page: 25, total_pages: 0 });
  const filterCatalog = ref({
    managers: [],
    people: [],
    addresses: [],
    stores: [],
    post_sale_statuses: [],
    sale_types: [],
    financial_statuses: [],
  });
  const loading = ref(false);
  const loadingCatalog = ref(false);
  const error = ref('');
  const lastUpdatedAt = ref(null);
  const currentTime = ref(Date.now());
  let refreshTimer;

  const totalRows = computed(() => meta.value.count || 0);
  const totalPages = computed(() => meta.value.total_pages || 0);
  const minutesSinceRefresh = computed(() => {
    if (!lastUpdatedAt.value) return null;

    return Math.max(
      0,
      Math.floor((currentTime.value - lastUpdatedAt.value.getTime()) / 60000)
    );
  });

  const notifyError = message => {
    error.value = message;
    if (notify) notify(message, 'error');
  };

  const requestParams = () => ({
    tab: tab.value,
    page: page.value,
    per_page: meta.value.per_page,
    ...filters.value,
    ordenacao:
      order.value.patch && order.value.id >= 0
        ? `${order.value.patch}_${order.value.id}`
        : '',
  });

  const load = async () => {
    loading.value = true;
    error.value = '';
    try {
      const response = await RealEstateFinanceAPI.list(requestParams());
      rows.value = response.data?.data || [];
      meta.value = response.data?.meta || meta.value;
      lastUpdatedAt.value = new Date();
      currentTime.value = Date.now();
    } catch {
      rows.value = [];
      notifyError(t('REAL_ESTATE_FINANCE.ERRORS.LOAD'));
    } finally {
      loading.value = false;
    }
  };

  const loadCatalog = async () => {
    loadingCatalog.value = true;
    try {
      const filtersResponse = await RealEstateFinanceAPI.filterCatalog();
      filterCatalog.value = filtersResponse.data || filterCatalog.value;
    } catch {
      notifyError(t('REAL_ESTATE_FINANCE.ERRORS.CATALOG'));
    } finally {
      loadingCatalog.value = false;
    }
  };

  const setTab = nextTab => {
    tab.value = nextTab;
    persistFinanceTab(nextTab);
    filters.value = {
      ...DEFAULT_FILTERS,
      filtro_endereco: filters.value.filtro_endereco,
      filtro_numero: filters.value.filtro_numero,
    };
    order.value = { ...DEFAULT_ORDER };
    page.value = 1;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters.value));
    window.localStorage.removeItem(ORDER_STORAGE_KEY);
  };

  const applyFilters = nextFilters => {
    filters.value = { ...DEFAULT_FILTERS, ...nextFilters };
    page.value = 1;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters.value));
  };

  const clearFilters = () => {
    applyFilters({ ...DEFAULT_FILTERS });
  };

  const setOrder = nextOrder => {
    order.value = { ...DEFAULT_ORDER, ...nextOrder };
    page.value = 1;
    window.localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order.value));
  };

  const clearOrder = () => {
    order.value = { ...DEFAULT_ORDER };
    page.value = 1;
    window.localStorage.removeItem(ORDER_STORAGE_KEY);
  };

  const changePage = nextPage => {
    page.value = Math.max(1, Math.min(nextPage, totalPages.value || 1));
  };

  watch([tab, filters, order, page], load, { deep: true });

  onMounted(() => {
    refreshTimer = window.setInterval(() => {
      currentTime.value = Date.now();
    }, 60000);
    loadCatalog();
    load();
  });
  onBeforeUnmount(() => window.clearInterval(refreshTimer));

  return {
    tab,
    filters,
    order,
    page,
    rows,
    filterCatalog,
    loading,
    loadingCatalog,
    error,
    totalRows,
    totalPages,
    minutesSinceRefresh,
    load,
    loadCatalog,
    setTab,
    applyFilters,
    clearFilters,
    setOrder,
    clearOrder,
    changePage,
  };
}
